import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  User, Phone, Loader2, ArrowRight, KeyRound, History,
  Heart, MapPin, Gift, LogOut, ChevronRight, Package, Star
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Customer Login/Profile Modal
 * Handles phone-based authentication with WhatsApp verification code
 */
export function CustomerAuthModal({
  open,
  onClose,
  restaurantId,
  restaurantName,
  themeColor = "#059669",
  onLogin,
  onLogout,
  customerSession
}) {
  const [step, setStep] = useState("phone"); // phone, code, profile
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [profile, setProfile] = useState(null);

  // Load existing session
  useEffect(() => {
    if (customerSession?.phone) {
      setPhone(customerSession.phone);
      setProfile(customerSession);
      setStep("profile");
    } else {
      setStep("phone");
      setProfile(null);
    }
  }, [customerSession, open]);

  // Countdown for resend code
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const requestCode = async () => {
    if (!phone || phone.length < 9) {
      toast.error("Digite um telefone válido");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/customer/request-code/${restaurantId}`, {
        phone
      });
      
      if (res.data.success) {
        toast.success(res.data.whatsapp_sent 
          ? "Código enviado para seu WhatsApp!" 
          : "Código gerado. Verifique seu WhatsApp.");
        setStep("code");
        setCountdown(60);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar código");
    }
    setLoading(false);
  };

  const verifyCode = async () => {
    if (!code || code.length !== 6) {
      toast.error("Digite o código de 6 dígitos");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/customer/verify-code/${restaurantId}`, {
        phone,
        code,
        name
      });
      
      if (res.data.success) {
        toast.success("Login realizado com sucesso!");
        setProfile(res.data.customer);
        setStep("profile");
        
        // Save to localStorage and notify parent
        const sessionData = {
          ...res.data.customer,
          token: res.data.session_token,
          orders: res.data.orders
        };
        localStorage.setItem(`kaiso_customer_${restaurantId}`, JSON.stringify(sessionData));
        onLogin?.(sessionData);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código inválido");
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem(`kaiso_customer_${restaurantId}`);
    setProfile(null);
    setPhone("");
    setCode("");
    setStep("phone");
    onLogout?.();
    toast.success("Logout realizado");
  };

  const formatPhone = (value) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, "");
    // Format as needed
    return digits;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto" data-testid="customer-auth-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" style={{ color: themeColor }} />
            {step === "profile" ? "Minha Conta" : "Entrar"}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Phone Input */}
        {step === "phone" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Digite seu telefone para receber um código de acesso via WhatsApp.
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Telefone (com DDD)</Label>
              <div className="flex gap-2">
                <Input
                  id="phone"
                  type="tel"
                  placeholder="11999999999"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  className="flex-1"
                  data-testid="customer-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Seu nome (opcional)</Label>
              <Input
                id="name"
                type="text"
                placeholder="Como podemos te chamar?"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="customer-name-input"
              />
            </div>

            <Button
              onClick={requestCode}
              disabled={loading || phone.length < 9}
              className="w-full h-11"
              style={{ backgroundColor: themeColor }}
              data-testid="request-code-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Receber código
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 2: Code Verification */}
        {step === "code" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Digite o código de 6 dígitos enviado para <strong>{phone}</strong>
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="code">Código de verificação</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                data-testid="customer-code-input"
              />
            </div>

            <Button
              onClick={verifyCode}
              disabled={loading || code.length !== 6}
              className="w-full h-11"
              style={{ backgroundColor: themeColor }}
              data-testid="verify-code-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Verificar código
                </>
              )}
            </Button>

            <div className="flex items-center justify-between text-sm">
              <button
                onClick={() => setStep("phone")}
                className="text-muted-foreground hover:underline"
              >
                Alterar número
              </button>
              
              {countdown > 0 ? (
                <span className="text-muted-foreground">
                  Reenviar em {countdown}s
                </span>
              ) : (
                <button
                  onClick={requestCode}
                  className="hover:underline"
                  style={{ color: themeColor }}
                >
                  Reenviar código
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Profile View */}
        {step === "profile" && profile && (
          <div className="space-y-4">
            {/* User Info */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: themeColor }}
              >
                {profile.name?.charAt(0)?.toUpperCase() || "👤"}
              </div>
              <div className="flex-1">
                <p className="font-bold">{profile.name || "Cliente"}</p>
                <p className="text-sm text-muted-foreground">{profile.phone}</p>
              </div>
              {profile.points > 0 && (
                <Badge className="bg-amber-100 text-amber-700 border-0">
                  <Gift className="w-3 h-3 mr-1" />
                  {profile.points} pts
                </Badge>
              )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <p className="text-xl font-black" style={{ color: themeColor }}>
                  {profile.total_orders || 0}
                </p>
                <p className="text-[10px] text-muted-foreground">Pedidos</p>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <p className="text-xl font-black" style={{ color: themeColor }}>
                  {profile.favorites?.length || 0}
                </p>
                <p className="text-[10px] text-muted-foreground">Favoritos</p>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <p className="text-xl font-black" style={{ color: themeColor }}>
                  {profile.addresses?.length || 0}
                </p>
                <p className="text-[10px] text-muted-foreground">Endereços</p>
              </div>
            </div>

            {/* Menu Options */}
            <div className="space-y-1">
              <button
                onClick={() => {/* TODO: Show orders */}}
                className="w-full flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors"
                data-testid="view-orders-btn"
              >
                <span className="flex items-center gap-3">
                  <History className="w-5 h-5 text-muted-foreground" />
                  <span>Meus Pedidos</span>
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>

              <button
                onClick={() => {/* TODO: Show favorites */}}
                className="w-full flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors"
                data-testid="view-favorites-btn"
              >
                <span className="flex items-center gap-3">
                  <Heart className="w-5 h-5 text-muted-foreground" />
                  <span>Favoritos</span>
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>

              <button
                onClick={() => {/* TODO: Show addresses */}}
                className="w-full flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors"
                data-testid="view-addresses-btn"
              >
                <span className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                  <span>Meus Endereços</span>
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>

              {profile.points > 0 && (
                <button
                  onClick={() => {/* TODO: Show rewards */}}
                  className="w-full flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors"
                  data-testid="view-rewards-btn"
                >
                  <span className="flex items-center gap-3">
                    <Gift className="w-5 h-5 text-muted-foreground" />
                    <span>Recompensas</span>
                  </span>
                  <Badge className="bg-amber-100 text-amber-700 text-xs">
                    {profile.points} pontos
                  </Badge>
                </button>
              )}

              {/* Link to full Customer Portal */}
              <a
                href={`/portal/${restaurantId}`}
                className="w-full flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100"
                data-testid="go-to-portal-btn"
              >
                <span className="flex items-center gap-3">
                  <User className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium text-emerald-700">Portal do Cliente</span>
                </span>
                <ChevronRight className="w-4 h-4 text-emerald-600" />
              </a>
            </div>

            {/* Logout */}
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full text-red-600 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair da conta
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Small button to show customer login status
 */
export function CustomerLoginButton({ 
  customerSession, 
  onClick, 
  themeColor = "#059669" 
}) {
  if (customerSession?.name) {
    return (
      <button
        onClick={onClick}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        data-testid="customer-profile-btn"
      >
        <div 
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: themeColor }}
        >
          {customerSession.name.charAt(0).toUpperCase()}
        </div>
        <span className="text-sm font-medium text-white max-w-[80px] truncate">
          {customerSession.name}
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-sm font-medium shadow-sm hover:opacity-90 transition-opacity"
      style={{ backgroundColor: themeColor }}
      data-testid="customer-login-btn"
    >
      <User className="w-4 h-4" />
      Entrar
    </button>
  );
}

/**
 * Favorite button for product cards
 */
export function FavoriteButton({ 
  productId, 
  isFavorite, 
  onToggle, 
  size = "sm",
  themeColor = "#059669"
}) {
  const [loading, setLoading] = useState(false);

  const handleClick = async (e) => {
    e.stopPropagation();
    setLoading(true);
    await onToggle?.(productId, !isFavorite);
    setLoading(false);
  };

  const sizeClass = size === "sm" ? "w-8 h-8" : "w-10 h-10";
  const iconSize = size === "sm" ? "w-4 h-4" : "w-5 h-5";

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={`${sizeClass} rounded-full flex items-center justify-center transition-all ${
        isFavorite 
          ? "bg-red-100 text-red-500 hover:bg-red-200" 
          : "bg-white/80 text-gray-400 hover:bg-white hover:text-red-500"
      }`}
      data-testid={`favorite-btn-${productId}`}
    >
      {loading ? (
        <Loader2 className={`${iconSize} animate-spin`} />
      ) : (
        <Heart className={`${iconSize} ${isFavorite ? "fill-current" : ""}`} />
      )}
    </button>
  );
}

export default CustomerAuthModal;
